import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, Layers, FileBarChart, Settings, Smartphone, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/services/apiClient';

const WMSDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalLocations: 0,
    totalBins: 0
  });

  const fetchStats = useCallback(async () => {
    try {
      const locResponse = await apiClient.get('/wms/locations');
      setStats({
        totalLocations: locResponse.data.total || 0,
        totalBins: 0
      });
    } catch (error) {
      console.error('Error fetching WMS stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Admin setup modules only - SFG/FG operations are in Dispatch Section
  const modules = [
    {
      id: 'design-transfer',
      title: 'Design Transfer',
      description: 'Transfer USNs from one FG Name to another',
      icon: ArrowLeftRight,
      route: '/wms/design-transfer',
      color: 'bg-purple-500'
    },
    {
      id: 'location-setup',
      title: 'Location Setup',
      description: 'Configure storage hierarchy (Location → Row → Rack → Level)',
      icon: MapPin,
      route: '/wms/location-setup',
      color: 'bg-blue-500'
    },
    {
      id: 'zone-mapping',
      title: 'ABC Zone Mapping',
      description: 'Map velocity classes to storage zones',
      icon: Layers,
      route: '/wms/zone-mapping',
      color: 'bg-green-500'
    },
    {
      id: 'projections',
      title: 'Monthly Projections',
      description: 'View and manage SKU-wise monthly projections',
      icon: FileBarChart,
      route: '/wms/projections',
      color: 'bg-orange-500'
    },
    {
      id: 'settings',
      title: 'WMS Settings',
      description: 'Configure auto-trigger time and other settings',
      icon: Settings,
      route: '/wms/settings',
      color: 'bg-gray-500'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">WMS Setup</h1>
                <p className="text-sm text-[#E8DCC8]">Admin Configuration</p>
              </div>
            </div>
          </div>
          <Button
            onClick={() => navigate('/wms/mobile')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="wms-mobile-view-btn"
          >
            <Smartphone className="w-4 h-4 mr-2" />
            Mobile View
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Storage Locations</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.totalLocations}</p>
              </div>
              <MapPin className="w-10 h-10 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Total Bins</p>
                <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{stats.totalBins}</p>
              </div>
              <Package className="w-10 h-10 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Module Cards */}
        <div>
          <h2 className="text-2xl font-bold text-[#1A1F2E] mb-6 font-display">Setup & Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {modules.map((module) => {
              const Icon = module.icon;
              return (
                <div
                  key={module.id}
                  onClick={() => navigate(module.route)}
                  className="bg-white rounded-lg border-2 border-[#E5DFD0] hover:border-[#2D5F4F] shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
                  data-testid={`wms-module-${module.id}`}
                >
                  <div className={`${module.color} p-4`}>
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-[#1A1F2E] mb-1 font-display">
                      {module.title}
                    </h3>
                    <p className="text-sm text-[#5A5F6E]">{module.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default WMSDashboard;
